/**
 * 当前登录用户与角色能力（前端侧）。
 *
 * ## 这不是安全边界
 *
 * 真正的权限判定在服务端（`server/src/authz.ts`）：所有写接口都挂了
 * `requireAdmin + requireCapability`，越权请求会拿到 403。
 * 这里做的一切只是**体验优化** —— 不该点的按钮别让用户点了才发现报错。
 *
 * ## 能力来自服务端，而不是前端的一张表
 *
 * 角色与权限矩阵现在可以在管理端维护。如果前端再保存一份 `ROLE_CAPS`，
 * 改完角色权限后两边必然漂移。因此登录 / `/api/auth/me` 直接返回
 * `capabilities`，`can()` 只查这个数组。
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { Capability, SessionUser } from './api'

export type { Capability } from './api'

/** 内置角色的中文名，仅用于旧会话或角色已被删除时的兜底展示。 */
const BUILTIN_ROLE_LABELS: Record<string, string> = {
  admin: '管理员',
  engineer: '工程师',
  operator: '操作员',
}

/** 角色中文名：优先用服务端返回的角色名。 */
export function roleLabel(role: string | undefined, roleName?: string): string {
  if (roleName) return roleName
  if (!role) return '—'
  return BUILTIN_ROLE_LABELS[role] ?? role
}

interface AuthContextValue {
  user: SessionUser | null
  role: string
  roleName: string
  capabilities: readonly Capability[]
  /** 当前用户是否具备某能力。未登录时一律 false。 */
  can: (cap: Capability) => boolean
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  role: '',
  roleName: '—',
  capabilities: [],
  // 默认拒绝：上下文尚未挂载时（如单页测试）不误伤渲染，同时不会出现
  // 「测试环境默认有全部权限」的假象。真正的拦截在服务端。
  can: () => false,
})

export function AuthProvider({
  user,
  children,
}: {
  user: SessionUser | null
  children: ReactNode
}) {
  const value = useMemo<AuthContextValue>(() => {
    const capabilities = user?.capabilities ?? []
    const role = user?.role ?? ''
    return {
      user,
      role,
      roleName: roleLabel(role, user?.roleName),
      capabilities,
      can: (cap) => (user ? capabilities.includes(cap) : false),
    }
  }, [user])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext)
}

/** 便捷钩子：仅取 can 函数。 */
export function useCan(): (cap: Capability) => boolean {
  return useAuth().can
}
